

// components/tracking/ShareSection.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { QRCodeCanvas } from "qrcode.react";
import {
  FaShareAlt, FaWhatsapp, FaEnvelope, FaTelegramPlane,
  FaLink, FaQrcode, FaCheck
} from 'react-icons/fa';

const ShareSection = ({ shareUrls, copyToClipboard, copySuccess }) => (
  <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
    <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
      <FaShareAlt className="text-blue-600" /> Share Tracking
    </h3>
    
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* QR Code Section */}
      <div className="lg:col-span-1">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 text-center border border-blue-100">
          <div className="bg-white p-3 rounded-lg inline-block shadow-sm mb-4">
            <QRCodeCanvas 
              value={window.location.href} 
              size={100} 
              level="M"
              includeMargin={true}
              bgColor="#ffffff"
              fgColor="#1e40af"
            />
          </div>
          <div className="text-sm text-gray-700">
            <div className="flex items-center justify-center gap-2 mb-1">
              <FaQrcode className="text-blue-600" />
              <span className="font-semibold">Scan QR Code</span>
            </div>
            <p className="text-gray-600">Open on mobile device</p>
          </div>
        </div>
      </div>
      
      {/* Share Buttons */}
      <div className="lg:col-span-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* WhatsApp */}
          <motion.a
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            href={shareUrls.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 group"
          >
            <div className="bg-white/20 p-2 rounded-lg">
              <FaWhatsapp className="text-xl" />
            </div>
            <div className="flex-1">
              <div className="font-semibold">WhatsApp</div>
              <div className="text-xs text-green-100">Share with contacts</div>
            </div>
            <div className="text-white/70 group-hover:text-white transition-colors">
              <FaShareAlt className="text-sm" />
            </div>
          </motion.a>

          {/* Email */}
          <motion.a
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            href={shareUrls.email}
            className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 group"
          >
            <div className="bg-white/20 p-2 rounded-lg">
              <FaEnvelope className="text-xl" />
            </div>
            <div className="flex-1">
              <div className="font-semibold">Email</div>
              <div className="text-xs text-blue-100">Send via email</div>
            </div>
            <div className="text-white/70 group-hover:text-white transition-colors">
              <FaShareAlt className="text-sm" />
            </div>
          </motion.a>

          {/* Telegram */}
          <motion.a
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            href={shareUrls.telegram}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 group"
          >
            <div className="bg-white/20 p-2 rounded-lg">
              <FaTelegramPlane className="text-xl" />
            </div>
            <div className="flex-1">
              <div className="font-semibold">Telegram</div>
              <div className="text-xs text-sky-100">Share on Telegram</div>
            </div>
            <div className="text-white/70 group-hover:text-white transition-colors">
              <FaShareAlt className="text-sm" />
            </div>
          </motion.a>

          {/* Copy Tracking Link */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={copyToClipboard}
            className={`flex items-center gap-3 p-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 group ${
              copySuccess 
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white' 
                : 'bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900 text-white'
            }`}
          >
            <div className={`p-2 rounded-lg ${copySuccess ? 'bg-white/20' : 'bg-white/20'}`}>
              {copySuccess ? (
                <FaCheck className="text-xl" />
              ) : (
                <FaLink className="text-xl" />
              )}
            </div>
            <div className="flex-1">
              <div className="font-semibold">
                {copySuccess ? "Link Copied!" : "Copy Tracking Link"}
              </div>
              <div className={`text-xs ${copySuccess ? 'text-emerald-100' : 'text-gray-300'}`}>
                {copySuccess ? "Ready to share" : "Copy to clipboard"}
              </div>
            </div>
            <div className={`transition-colors ${copySuccess ? 'text-white' : 'text-white/70 group-hover:text-white'}`}>
              {copySuccess ? (
                <FaCheck className="text-sm" />
              ) : (
                <FaLink className="text-sm" />
              )}
            </div>
          </motion.button>
        </div>
        
        {/* Quick Actions */}
        <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="text-sm text-gray-600 text-center">
            <p className="font-medium text-gray-700 mb-1">💡 Quick Tip</p>
            <p>Share this tracking link with recipients so they can monitor delivery progress in real-time</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default ShareSection;

// ============================================================================